import classNames from 'classnames';
import { useCallback } from 'react';
import { Slider as RASlider, SliderThumb, SliderTrack, type SliderTrackRenderProps } from 'react-aria-components';
import { getValueIndex, SharedSliderProps, thumbSize } from './sharedSliderHelpers';
import SliderHeader from './SliderHeader';
import SliderPoints from './SliderPoints';
import styles from './styles/styles.module.scss';

type RangeSliderProps = SharedSliderProps<[number, number]>;

const RangeSlider = ({
  className,
  compactSubHeader,
  disabled,
  formatHeaderValue,
  headerValueTextVariant,
  label,
  labelTextVariant,
  marks,
  onChange,
  onChangeEnd,
  subHeader,
  subHeaderTextVariant,
  value,
  ariaLabelledby
}: RangeSliderProps) => {
  const sliderTrack = useCallback(
    (props: SliderTrackRenderProps) => {
      const { state } = props;
      const lowerPercent = state.getThumbPercent(0);
      const upperPercent = state.getThumbPercent(1);
      const leftPercent = lowerPercent * 100;
      const widthPercent = (upperPercent - lowerPercent) * 100;
      const width = `calc(${widthPercent}% + ${thumbSize / 2}px)`; // See comment in the scss for explanation of the calc call

      const lowerIndex = state.getThumbValue(0);
      const upperIndex = state.getThumbValue(1);

      // When using a range slider, the second SliderThumb renders above the first one by default.
      // This causes a bug where the user is unable move either SliderThumb if they drag the upper range and then the lower range to the max value.
      // To solve this, render the "lower" SliderThumb on top when both marks are maxed out.
      const renderAbove = lowerPercent === 1;

      return (
        <>
          <div className={styles.track} />
          <div className={styles.trackFill} style={{ left: `${leftPercent}%`, width }} />
          <SliderPoints getIsFilled={index => index >= lowerIndex && index <= upperIndex} marks={marks} />
          {/* The library applies the SliderThumb transform via style not className, so we have to do the same to override it without !important */}
          <SliderThumb className={styles.thumb} index={0} style={{ transform: 'translateX(-50%)', zIndex: renderAbove ? 1 : 'unset' }} />
          <SliderThumb className={styles.thumb} index={1} style={{ transform: 'translateX(-50%)' }} />
        </>
      );
    },
    [marks]
  );

  return (
    <RASlider
      className={classNames(styles.container, className)}
      isDisabled={disabled}
      maxValue={marks.length - 1}
      minValue={0}
      onChange={([lowerIndex, upperIndex]) => {
        onChange([marks[lowerIndex].value, marks[upperIndex].value] as [number, number]);
      }}
      onChangeEnd={([lowerIndex, upperIndex]) => {
        onChangeEnd?.([marks[lowerIndex].value, marks[upperIndex].value] as [number, number]);
      }}
      value={[getValueIndex(marks, value[0]), getValueIndex(marks, value[1])]}
      aria-labelledby={ariaLabelledby}
    >
      <SliderHeader
        compactSubHeader={compactSubHeader}
        headerValueTextVariant={headerValueTextVariant}
        label={label}
        labelTextVariant={labelTextVariant}
        sliderOutputChildren={
          formatHeaderValue
            ? ({ state }) => {
                const outputValue: [number, number] = [marks[state.getThumbValue(0)].value, marks[state.getThumbValue(1)].value];
                return formatHeaderValue(outputValue);
              }
            : undefined
        }
        subHeader={subHeader}
        subHeaderTextVariant={subHeaderTextVariant}
      />
      <SliderTrack className={styles.trackContainer}>{sliderTrack}</SliderTrack>
    </RASlider>
  );
};

export default RangeSlider;
